import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

// models
import { Element } from '../../../../../equipment/models/elements/element';
import { IPlanogram } from '../../../../models/planogram';
import { IPlanogramPlanData } from '../../../../models/planogramData';
import { GeneratedRule } from '../models/generatedRule';
import { Languages } from '../../../../../core/models/languages';
import { MixpanelEvents } from '../../../../../core/models/mixpanelEvents';
import { Product } from '../../../../models/product';
import { RuleConfiguration } from '../models/ruleConfiguration';
import { ShelfElement } from '../../../../models/elements/shelfElement';

// services
import { RulesService } from '../../../../../landing/rules/rules.service';
import { LayoutRulesService } from '../../../../../core/services/layout-rules.service';
import { MixpanelService } from '../../../../../core/services/mixpanel.service';

export type Preset = { title: string; instructions: string; getInstructions: () => Observable<any>; };

@Component({
    selector: 'app-ai-rule[planogram]',
    templateUrl: './ai-rule.component.html',
    styleUrls: ['./ai-rule.component.scss'],
    providers: [RulesService],
    standalone: false
})
export class AIRuleComponent implements OnInit {

	@Input('instructions') set AIInstructions(value: string) {
		if (this.instructions != value) {
			this.answer = 'RULES.AI.ANSWER';
			this.reactionAvailable = false;
			this.reactionClicked = 'empty';
		}
		this.instructions = value;
	};
	@Input('plan') plan: number;
	@Input('planogram') planogram: IPlanogram;
	@Input('presets') presets: Array<Preset> = [];
	@Input('products') products: Array<Product> = [];

	@Output('startGenerating') startGenerating: EventEmitter<void> = new EventEmitter<void>();
	@Output('endGenerating') endGenerating: EventEmitter<RuleConfiguration> = new EventEmitter<RuleConfiguration>();

	public answer: string = 'RULES.AI.ANSWER';
	public generating: boolean = false;
	public instructions: string = '';
	public reactionAvailable: boolean = false;
	public reactionClicked: 'empty' | 'like' | 'dislike' = 'empty';

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private rulesService: RulesService,
		private layoutRulesService: LayoutRulesService,
		private mixpanelService: MixpanelService
	) {}

	public ngOnInit(): void {}

	clickInfo(): void {
		let url: string;

		switch(this.translate.currentLang) {
			case Languages.Ukrainian: {
				url = 'https://helpdesk.planohero.com/uk/article/ai-generaciya-pravila-vikladki-1pvttct/';
				break;
			}
			case Languages.Russian: {
				url = 'https://helpdesk.planohero.com/ru/article/ai-generaciya-pravila-vykladki-dpemvm/';
				break;
			}
			default: {
				url = 'https://helpdesk.planohero.com/en/article/ai-generation-of-layout-rules-1oj3l5t/';
				break;
			}
		}

		this.mixpanelService.trackPage(MixpanelEvents.GenerateRuleInfo);
		window.open(url, '_blank');
	}

	clickPreset(preset: Preset): void {
		this.translate.get(preset.title).subscribe((text: string) => {
			this.mixpanelService.trackPage(MixpanelEvents.GenerateRulePreset, { text: text });
		})

		preset.getInstructions().subscribe((text: string) => {
			this.generateRule(text);
		});
	}

	clickReaction(type: 'like' | 'dislike'): void {
		if (this.reactionClicked == type) return;

		this.reactionClicked = type;

		this.mixpanelService.trackPage(MixpanelEvents.GenerateRuleReaction, {
			type: type,
			instructions: this.instructions,
			answer: this.answer
		});
	}

	generateRule(instructions: string): void {
		let request: Observable<GeneratedRule>;

		const shelves: Array<Element> = this.planogram.stand.availableElements.filter((el: Element) => el.is(ShelfElement)),
			maxShelvesNumber: number = Math.max(
				...this.planogram.stand.availableElements
					.filter((el: Element) => ['RectangleBase', 'TrapezeBase'].includes(el.parent.type))
					.map((el: Element) => [el, ...shelves.filter((sh: Element) => sh.depend?.depend == el)])
					.map((group: Array<Element>) => group.length)
			);

		if (this.router.url.includes('landing')) {
			request = this.rulesService.generateRule(
				instructions,
				null,
				maxShelvesNumber,
				this.route.snapshot.queryParams.lang || Languages.English
			);
		} else {
			let store: number = this.planogram.planogram_stand[0].planogram_shop;

			if (this.plan) {
				const planogram: IPlanogramPlanData = this.planogram.planogram_stand
					.find((ps: IPlanogramPlanData) => ps.planogram_id == this.plan);
				if (planogram) store = planogram.planogram_shop;
			}

			request = this.layoutRulesService.generateRule(
				instructions,
				Number(store) || null,
				maxShelvesNumber
			)
		}

		this.instructions = instructions;
		this.generating = true;
		this.answer = 'RULES.AI.ANSWER';
		this.reactionAvailable = false;
		this.reactionClicked = 'empty';

		this.startGenerating.emit();

		request.subscribe(async (conf: GeneratedRule) => {
			this.generating = false;
			this.reactionAvailable = true;

			if (conf.error) {
				this.answer = conf.error;
				this.endGenerating.emit();
			} else {
				this.answer = 'RULES.AI.GENERATED';
				this.endGenerating.emit(Object.assign(conf.result, { instructions: this.instructions }));
			}
		});

		this.mixpanelService.trackPage(MixpanelEvents.GenerateRule, { instructions: this.instructions });
	}
}